import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { ChevronLeft, User, Save, AlertTriangle, Users, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMORBIDITIES = [
  "Diabetes Mellitus", "Hipertensão Arterial",
  "Doença Renal Crônica", "Cardiopatia", "Obesidade", "Imobilidade", "Outras"
];

const RISK_FACTORS = [
  { key: "smoking", label: "Tabagismo" },
  { key: "cancer", label: "Neoplasia / Câncer" },
  { key: "immunodeficiency", label: "Imunodeficiência" },
  { key: "venous_insufficiency", label: "Insuficiência Venosa" },
  { key: "arterial_insufficiency", label: "Insuficiência Arterial" },
];

export default function NewPatientPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", social_name: "", cpf: "", birth_date: "", gender: "",
    address: "", city: "", phone: "",
    health_unit_id: "", responsible_acs_id: "",
    assigned_nurse_id: "", assigned_acs_id: "", assigned_doctor_id: "",
    smoking: false, cancer: false, immunodeficiency: false,
    venous_insufficiency: false, arterial_insufficiency: false,
    comorbidities: [], other_comorbidities: "", medications: "", previous_lesions: "",
    glycemic_control: "desconhecido", last_hba1c: ""
  });
  const [healthUnits, setHealthUnits] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/health-units`, { withCredentials: true }),
      axios.get(`${API}/users/professionals`, { withCredentials: true }),
    ]).then(([unitsResp, profsResp]) => {
      setHealthUnits(unitsResp.data || []);
      setProfessionals(profsResp.data || []);
    }).catch(() => {});
  }, []);

  const nurses = professionals.filter(p => ["enfermeiro", "tecnico"].includes(p.role));
  const acs = professionals.filter(p => p.role === "acs");
  const doctors = professionals.filter(p => ["medico", "especialista"].includes(p.role));
  const profName = (p) => p.full_name || p.name;

  const setField = (field, value) => {
    setForm(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: "" }));
  };

  const toggleRiskFactor = (key) => setForm(p => ({ ...p, [key]: !p[key] }));

  const toggleComorbidity = (c) => {
    setForm(p => ({
      ...p,
      comorbidities: p.comorbidities.includes(c)
        ? p.comorbidities.filter(x => x !== c)
        : [...p.comorbidities, c]
    }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Nome é obrigatório";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    try {
      const resp = await axios.post(`${API}/patients`, form, { withCredentials: true });
      navigate(`/patients/${resp.data.patient_id}`);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div data-testid="new-patient-page" className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6 animate-fade-in">
          <button onClick={() => navigate("/patients")} className="p-2 rounded-lg hover:bg-secondary transition-all">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Novo Paciente
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Personal info */}
          <div className="bg-white rounded-xl border border-border p-5 space-y-4 animate-fade-in">
            <div className="flex items-center gap-2 mb-1">
              <User className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                Dados Pessoais
              </h2>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Nome Completo *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => setField("name", e.target.value)}
                placeholder="Nome do paciente"
                className={`mt-1 w-full h-12 border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors.name ? "border-destructive" : "border-border"}`}
                data-testid="patient-name-input"
              />
              {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Nome Social
              </label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Como o paciente prefere ser chamado(a)</p>
              <input
                type="text"
                value={form.social_name}
                onChange={e => setField("social_name", e.target.value)}
                placeholder="Nome preferido (opcional)"
                className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="patient-social-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">CPF</label>
                <input
                  type="text"
                  value={form.cpf}
                  onChange={e => setField("cpf", e.target.value)}
                  placeholder="000.000.000-00"
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="patient-cpf-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Data de Nascimento</label>
                <input
                  type="date"
                  value={form.birth_date}
                  onChange={e => setField("birth_date", e.target.value)}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="patient-birthdate-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Sexo</label>
              <select
                value={form.gender}
                onChange={e => setField("gender", e.target.value)}
                className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="patient-gender-select"
              >
                <option value="">Selecionar</option>
                <option value="masculino">Masculino</option>
                <option value="feminino">Feminino</option>
                <option value="outro">Outro</option>
              </select>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-xl border border-border p-5 space-y-4 animate-fade-in">
            <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Contato e Endereço</h2>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Telefone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => setField("phone", e.target.value)}
                placeholder="(00) 00000-0000"
                className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="patient-phone-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Endereço</label>
              <input
                type="text"
                value={form.address}
                onChange={e => setField("address", e.target.value)}
                placeholder="Rua, número, bairro"
                className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="patient-address-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Cidade</label>
              <input
                type="text"
                value={form.city}
                onChange={e => setField("city", e.target.value)}
                className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="patient-city-input"
              />
            </div>
            {healthUnits.length > 0 && (
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Unidade de Saúde</label>
                <select
                  value={form.health_unit_id}
                  onChange={e => setField("health_unit_id", e.target.value)}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="patient-healthunit-select"
                >
                  <option value="">Selecionar unidade</option>
                  {healthUnits.map(u => (
                    <option key={u.unit_id} value={u.unit_id}>{u.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Clinical info */}
          <div className="bg-white rounded-xl border border-border p-5 space-y-4 animate-fade-in">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Fatores de Risco</h2>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">Marque todos que se aplicam ao paciente</p>
            <div className="grid grid-cols-2 gap-2">
              {RISK_FACTORS.map(({ key, label }) => (
                <label
                  key={key}
                  className={`flex items-center gap-2 p-3 border rounded-xl cursor-pointer transition-all ${
                    form[key] ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"
                  }`}
                  data-testid={`risk-factor-${key}`}
                >
                  <input
                    type="checkbox"
                    checked={form[key]}
                    onChange={() => toggleRiskFactor(key)}
                    className="w-4 h-4 accent-primary"
                  />
                  <span className="text-xs font-medium text-foreground">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Clinical info */}
          <div className="bg-white rounded-xl border border-border p-5 space-y-4 animate-fade-in">
            <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Comorbidades e Medicações</h2>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                Comorbidades
              </label>
              <div className="grid grid-cols-2 gap-2">
                {COMORBIDITIES.map(c => (
                  <label
                    key={c}
                    className="flex items-center gap-2 p-2.5 border border-border rounded-lg cursor-pointer hover:bg-secondary/50 transition-all"
                    data-testid={`comorbidity-${c.replace(/\s/g, "-")}`}
                  >
                    <input
                      type="checkbox"
                      checked={form.comorbidities.includes(c)}
                      onChange={() => toggleComorbidity(c)}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-xs text-foreground">{c}</span>
                  </label>
                ))}
              </div>
              {form.comorbidities.includes("Outras") && (
                <div className="mt-3 animate-fade-in">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Especifique outras comorbidades</label>
                  <input
                    type="text"
                    value={form.other_comorbidities}
                    onChange={e => setField("other_comorbidities", e.target.value)}
                    placeholder="Ex: DPOC, fibromialgia, anemia crônica..."
                    className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    data-testid="other-comorbidities-input"
                  />
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Medicações em Uso</label>
              <textarea
                value={form.medications}
                onChange={e => setField("medications", e.target.value)}
                placeholder="Liste as medicações atuais..."
                rows={3}
                className="mt-1 w-full border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                data-testid="patient-medications-textarea"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Lesões Anteriores</label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Histórico de lesões e tratamentos prévios</p>
              <textarea
                value={form.previous_lesions}
                onChange={e => setField("previous_lesions", e.target.value)}
                placeholder="Descreva lesões anteriores e como foram tratadas..."
                rows={3}
                className="mt-1 w-full border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                data-testid="patient-previous-lesions-textarea"
              />
            </div>
          </div>

          {/* Controle Glicêmico — apenas para diabéticos */}
          {form.comorbidities.includes("Diabetes Mellitus") && (
            <div className="bg-white rounded-xl border border-primary/20 p-5 space-y-4 animate-fade-in">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Controle Glicêmico</h2>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">Diabetes detectado</span>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">Status do Controle</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "adequado", label: "Adequado", cls: "bg-green-50 border-green-300 text-green-800" },
                    { value: "inadequado", label: "Inadequado", cls: "bg-red-50 border-red-300 text-red-800" },
                    { value: "desconhecido", label: "Desconhecido", cls: "bg-gray-50 border-gray-300 text-gray-600" },
                  ].map(opt => (
                    <label key={opt.value}
                      className={`flex items-center justify-center p-3 border rounded-xl cursor-pointer text-sm font-medium transition-all ${
                        form.glycemic_control === opt.value ? opt.cls : "border-border text-muted-foreground hover:bg-secondary/50"
                      }`}
                      data-testid={`glycemic-${opt.value}`}
                    >
                      <input type="radio" value={opt.value} checked={form.glycemic_control === opt.value}
                        onChange={() => setField("glycemic_control", opt.value)} className="sr-only" />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Último HbA1c (%)</label>
                <input type="text" value={form.last_hba1c} onChange={e => setField("last_hba1c", e.target.value)}
                  placeholder="Ex: 7.2%" className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="last-hba1c-input" />
              </div>
            </div>
          )}

          {/* Equipe responsável */}
          <div className="bg-white rounded-xl border border-border p-5 space-y-4 animate-fade-in">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Equipe Responsável</h2>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">Vincule os profissionais que acompanharão este paciente</p>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Enfermeiro(a)</label>
              <select
                value={form.assigned_nurse_id}
                onChange={e => setField("assigned_nurse_id", e.target.value)}
                className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="assigned-nurse-select"
              >
                <option value="">Selecionar enfermeiro(a)</option>
                {nurses.map(p => (
                  <option key={p.user_id} value={p.user_id}>{profName(p)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Agente Comunitário de Saúde</label>
              <select
                value={form.assigned_acs_id}
                onChange={e => setField("assigned_acs_id", e.target.value)}
                className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="assigned-acs-select"
              >
                <option value="">Selecionar ACS</option>
                {acs.map(p => (
                  <option key={p.user_id} value={p.user_id}>{profName(p)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Médico(a) Responsável</label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Será notificado(a) em caso de evolução crítica identificada pela IA</p>
              <select
                value={form.assigned_doctor_id}
                onChange={e => setField("assigned_doctor_id", e.target.value)}
                className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="assigned-doctor-select"
              >
                <option value="">Selecionar médico(a)</option>
                {doctors.map(p => (
                  <option key={p.user_id} value={p.user_id}>{profName(p)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pb-4 animate-fade-in">
            <button
              type="button"
              onClick={() => navigate("/patients")}
              className="flex-1 py-3.5 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-60"
              data-testid="save-patient-button"
            >
              <Save className="w-4 h-4" />
              {saving ? "Salvando..." : "Cadastrar Paciente"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
